import type { ReactNode } from 'react';
import { Card, cx, CountChip, PageHeader } from './ui';
import { Icon } from './icons';

/**
 * The two things a job, an estimate and an invoice all have: a short life with
 * known stages, and a history of what happened to it.
 *
 * Both were written on the job page first. Copying them onto the estimate and
 * the invoice would have meant three answers to "which step is next" and three
 * places to fix a timeline that renders its spine wrong — so they live here,
 * and the pages only supply their own steps and entries.
 */

// --- the heading a record page opens with -----------------------------------

/**
 * One heading for the job, the estimate, the invoice and the customer.
 *
 * All four open the same way — where am I, what is this, what state is it in,
 * and what can I do about it — and all four had their own copy of it. Four
 * copies is four answers to "how big is a record title", which is the kind of
 * drift that makes an application feel assembled rather than designed.
 *
 * It renders `PageHeader`, so a record page and a list page share a heading
 * rather than resembling one another.
 */
export function RecordHeader({
  eyebrow,
  number,
  title,
  titleTail,
  badges,
  subtitle,
  actions,
  avatar,
}: {
  /** The category word: "Job details", "Invoice", "Customer profile". */
  eyebrow: string;
  /** Shown after the eyebrow in a lighter weight: "#1010". */
  number?: string;
  title: ReactNode;
  /**
   * The part of the title that is a label rather than a name — "#1023" after
   * "Invoice". Set in the same size at a lighter weight, so the heading reads
   * as one phrase with one emphasis.
   */
  titleTail?: ReactNode;
  badges?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  avatar?: ReactNode;
}) {
  return (
    <PageHeader
      avatar={avatar}
      eyebrow={
        <>
          {eyebrow}
          {number && <span className="tracking-[0.06em] text-ink-3">{number}</span>}
        </>
      }
      title={
        <span className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="min-w-0 truncate">
            {title}
            {titleTail && <span className="font-medium text-ink-2"> {titleTail}</span>}
          </span>
          {badges}
        </span>
      }
      subtitle={subtitle}
      actions={actions}
    />
  );
}

// --- the flow bar -----------------------------------------------------------

export interface FlowStep {
  key: string;
  label: string;
  icon: ReactNode;
  /** Has this stage been reached? */
  done: boolean;
  /** The stage was reached and went badly: declined, voided. */
  failed?: boolean;
  /** A date, a number, a balance — whatever makes the stage concrete. */
  sub?: string | null;
  /** Omit or pass null when this stage cannot be taken from here. */
  onClick?: (() => void) | null;
}

/**
 * Done, here, or not yet.
 *
 * Two layouts, because two stages and six stages are different pictures. A
 * long flow is a track of circles with their labels beneath — that is the only
 * way six of them fit. A short one puts the label beside its circle, which is
 * easier to read and stops two icons sitting a metre apart with nothing in
 * between.
 *
 * Which steps may be taken is never decided here: a page passes `onClick` only
 * for a step its own rules allow.
 */
export function FlowBar({
  steps,
  busy,
  title = 'Progress',
  status,
  className,
}: {
  steps: FlowStep[];
  busy?: boolean;
  title?: string;
  /** The short summary on the right: "Completed & paid", "Ready when you are". */
  status?: ReactNode;
  className?: string;
}) {
  const nextIndex = steps.findIndex((s) => !s.done);
  // Where the record actually is: the last stage it has reached.
  const currentIndex = steps.reduce((last, step, i) => (step.done ? i : last), -1);
  const short = steps.length <= 2;

  return (
    <section
      className={cx(
        'rounded-card border border-line bg-surface shadow-[0_2px_3px_rgb(37_59_45/0.012)]',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3 px-[21px] pt-[17px]">
        <h2 className="font-display text-[16px] font-bold tracking-[-0.015em] text-ink">{title}</h2>
        {status && <span className="text-[12px] text-ink-3">{status}</span>}
      </div>

      {short ? (
        <ol className="flex items-center justify-center gap-6 px-[30px] py-7">
          {steps.map((step, i) => (
            <li key={step.key} className="contents">
              {i > 0 && (
                <span
                  className={cx(
                    'h-px min-w-[20px] max-w-[110px] flex-1',
                    steps[i - 1]!.failed
                      ? 'bg-danger/40'
                      : steps[i - 1]!.done
                        ? 'bg-accent/40'
                        : 'bg-line-2',
                  )}
                  aria-hidden
                />
              )}
              <div className="flex shrink-0 items-center gap-3">
                <StepCircle
                  step={step}
                  state={step.failed ? 'failed' : step.done ? 'done' : i === nextIndex ? 'next' : 'future'}
                  busy={busy}
                  size="lg"
                />
                <div className="min-w-0">
                  <div
                    className={cx(
                      'text-[14px] font-semibold',
                      step.failed
                        ? 'text-danger'
                        : step.done
                          ? 'text-ink'
                          : i === nextIndex
                            ? 'text-accent'
                            : 'text-ink-4',
                    )}
                  >
                    {step.label}
                  </div>
                  {step.sub && <div className="mt-1 text-[12px] text-ink-3">{step.sub}</div>}
                </div>
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <ol className="flex items-start px-[21px] pt-6 pb-7">
          {steps.map((step, i) => (
            <li key={step.key} className="flex min-w-0 flex-1 flex-col items-center">
              <div className="flex w-full items-center">
                <Rule
                  hidden={i === 0}
                  on={i > 0 && steps[i - 1]!.done}
                  bad={i > 0 && Boolean(steps[i - 1]!.failed)}
                />
                <StepCircle
                  step={step}
                  state={
                    step.failed
                      ? 'failed'
                      : i === currentIndex
                        ? 'current'
                        : step.done
                          ? 'done'
                          : i === nextIndex
                            ? 'next'
                            : 'future'
                  }
                  busy={busy}
                />
                <Rule hidden={i === steps.length - 1} on={step.done} bad={Boolean(step.failed)} />
              </div>
              <div
                className={cx(
                  'mt-3 max-w-full truncate text-[13px] font-semibold',
                  step.failed
                    ? 'text-danger'
                    : step.done
                      ? 'text-ink'
                      : i === nextIndex
                        ? 'text-accent'
                        : 'text-ink-4',
                )}
              >
                {step.label}
              </div>
              <div className="mt-1 h-[15px] max-w-full truncate text-[12px] tnum text-ink-3">
                {step.sub ?? ''}
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

type StepState = 'done' | 'current' | 'next' | 'future' | 'failed';

/**
 * The circle itself.
 *
 * A halo — a ring of canvas around the circle — sits under every one of them,
 * so the connecting line stops short of the glyph instead of running into it.
 */
function StepCircle({
  step,
  state,
  busy,
  size = 'md',
}: {
  step: FlowStep;
  state: StepState;
  busy?: boolean;
  size?: 'md' | 'lg';
}) {
  const clickable = Boolean(step.onClick) && !busy;
  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={() => step.onClick?.()}
      title={clickable ? step.label : undefined}
      className={cx(
        'grid shrink-0 place-items-center rounded-full border transition-colors',
        'shadow-[0_0_0_5px_var(--color-canvas)]',
        size === 'lg' ? 'size-10' : 'size-9',
        state === 'failed' && 'border-danger bg-danger text-white',
        state === 'current' && 'border-accent bg-accent text-white',
        state === 'done' && 'border-accent bg-accent text-white',
        state === 'next' && 'border-accent-line bg-accent-soft text-accent',
        state === 'future' && 'border-line-2 bg-raised text-ink-4',
        clickable ? 'cursor-pointer hover:border-accent hover:text-accent' : 'cursor-default',
      )}
    >
      {step.icon}
    </button>
  );
}

/**
 * The connector between two steps: lit once the step behind it is done.
 *
 * The outer two are drawn but left blank. They keep every circle centred over
 * its own label — take them out and the first and last steps shift inward —
 * while the track itself runs only between circles, not out to the card's
 * edges.
 */
function Rule({ on, bad, hidden }: { on: boolean; bad: boolean; hidden?: boolean }) {
  return (
    <span
      className={cx(
        'h-px min-w-0 flex-1',
        hidden ? 'bg-transparent' : bad ? 'bg-danger/40' : on ? 'bg-accent/45' : 'bg-line',
      )}
      aria-hidden
    />
  );
}

/** A 20×20 stroked glyph, the only kind the flow bar uses. */
export function flowIcon(path: string) {
  return (
    <svg viewBox="0 0 20 20" fill="none" className="size-[18px]" aria-hidden>
      <path
        d={path}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** The glyphs the three record pages share, so a "sent" step looks the same everywhere. */
export const FLOW_ICONS = {
  schedule: 'M3 7.5h14M6 2.5v3M14 2.5v3M3.5 4.5h13v13h-13z',
  enRoute:
    'M2.5 13.5h1v-6l2-3.5h7l2 3.5v6h1M5 13.5h10M6.5 13.5a1.5 1.5 0 1 0 3 0 1.5 1.5 0 0 0-3 0ZM11.5 13.5a1.5 1.5 0 1 0 3 0 1.5 1.5 0 0 0-3 0Z',
  start: 'M7 5.5v9l7-4.5z',
  finish: 'M6.5 6.5h7v7h-7z',
  send: 'M3 10 17 4l-5.5 12-1.5-5z',
  pay: 'M2.5 6.5h15v8h-15zM2.5 9h15',
  approve: 'M4.5 10.5 8 14l7.5-8',
} as const;

// --- the activity feed ------------------------------------------------------

export interface FeedEntry {
  id: string;
  /** ISO timestamp; the feed sorts on it, newest first. */
  at: string;
  tone: 'status' | 'money' | 'muted';
  title: string;
  meta: string;
  detail?: string | null;
}

/**
 * One chronological column of what happened, newest first.
 *
 * Events reach a page from several tables and several objects, but to whoever
 * is reading it they are one story — so a page hands over a flat list and this
 * sorts it. Notes are deliberately never in it: they have their own card with
 * the box you write them in, and the same text in two columns reads as a bug.
 */
export function ActivityFeed({
  entries,
  title = 'Activity feed',
  empty = 'Nothing has happened yet.',
}: {
  entries: FeedEntry[];
  title?: string;
  empty?: string;
}) {
  const sorted = [...entries].sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));

  return (
    <Card
      title={title}
      action={sorted.length > 0 ? <CountChip value={sorted.length} /> : undefined}
      divided
      bodyClassName="px-[21px] pt-[19px] pb-[21px]"
    >
      {sorted.length === 0 ? (
        <p className="py-3 text-[13px] text-ink-4">{empty}</p>
      ) : (
        <ol className="max-h-[640px] overflow-y-auto">
          {sorted.map((entry, i) => (
            <li key={entry.id} className="relative flex gap-3 pb-5 last:pb-0">
              {/* The spine, drawn between markers rather than past the last one. */}
              {i < sorted.length - 1 && (
                <span className="absolute top-6 bottom-0 left-[11px] w-px bg-line" aria-hidden />
              )}
              <span
                className={cx(
                  'relative grid size-[23px] shrink-0 place-items-center rounded-full',
                  entry.tone === 'money'
                    ? 'bg-good-soft text-good'
                    : 'bg-surface text-ink-4 ring-1 ring-line-2 ring-inset',
                )}
                aria-hidden
              >
                {entry.tone === 'money' ? (
                  <Icon name="check" size={13} />
                ) : (
                  <span className="size-[5px] rounded-full bg-current" />
                )}
              </span>

              <div className="min-w-0 flex-1">
                <div
                  className={cx(
                    'text-[14px] leading-snug font-semibold',
                    entry.tone === 'money' ? 'text-good' : 'text-ink',
                    entry.tone === 'muted' && 'font-medium text-ink-3',
                  )}
                >
                  {entry.title}
                </div>
                <div className="mt-1.5 text-[12px] leading-relaxed text-ink-3">
                  {entry.meta}
                  {entry.meta && ' · '}
                  {formatFeedTime(entry.at)}
                </div>
                {entry.detail && (
                  <div className="mt-2 inline-block rounded-[5px] border border-line bg-raised px-2 py-0.5 text-[12px] text-ink-3">
                    {entry.detail}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

function formatFeedTime(value: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

// --- small shared formatters ------------------------------------------------

export function shortDate(value: string): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(
    new Date(value),
  );
}

export function shortTime(value: string): string {
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(
    new Date(value),
  );
}
